import { Component, OnInit, AfterViewInit, ViewEncapsulation, OnDestroy, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { CommonService } from '../../shared/common.service';
import { ToastService } from '../../shared/toast.service';
import { LoaderService } from '../../shared/loader.service';
import { SeoService } from '../../shared/seo.service';

interface CommunitySession {
  _id: string;
  title: string;
  description: string;
  quote: string;
  causeLabel: string;
  charity: string;
  price: number;
  totalSpots: number;
  bookedSpots: number;
  availableSpots: number;
  monthTag: string;
  sessions: Array<{
    _id: string;
    date: string;
    startTime: string;
    endTime: string;
  }>;
  instructor?: any;
  status?: string;
}

@Component({
  selector: 'app-community',
  templateUrl: './community.component.html',
  styleUrls: ['./community.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class CommunityComponent implements OnInit, AfterViewInit, OnDestroy {

  // ==================== DATA PROPERTIES ====================
  currentMonthSession: CommunitySession | null = null;
  upcomingSession: CommunitySession | null = null; 
  calendarSessions: CommunitySession[] = [];
  
  // ==================== UI STATE ====================
  loadingSessions: boolean = true;
  currentMonth: string = '';
  currentYear: number = new Date().getFullYear();
  today: Date = new Date();
  
  // Month order for calendar
  calendarMonths: { monthName: string; year: number; monthIndex: number; label: string }[] = [];
sessionsByMonthKey: { [key: string]: CommunitySession[] } = {};

  // ==================== MODAL STATE ====================
  showRegistrationModal: boolean = false;
  selectedSession: CommunitySession | null = null;
  selectedSessionDetail: any = null;
  
  // ==================== REGISTRATION FORM ====================
  registrationForm = {
    firstName: '',
    lastName: '',
    email: '',
    agreeToTerms: false,
    emailFollowUp: false  
  };

  constructor(
    private router: Router,
    private commonService: CommonService,
    private toast: ToastService,
    private loader: LoaderService,
    private seoService: SeoService
  ) {}

  ngOnInit(): void {
    this.seoService.applySeo({
    title: 'NEXGEN Coaching Community | Network with Certified Coaches',
    description: 'Join the exclusive NEXGEN Executive Coaching Academy community. Connect, collaborate, and grow with fellow ICF-aligned executive coaches. Access resources, peer support & networking for your coaching practice.',
    keywords: 'NEXGEN coaching community, executive coach network, ICF coach community, coaching academy members, NEXGEN coach forum',
    canonicalUrl: 'https://academy.nexgencoaches.com/community',
    ogTitle: 'NEXGEN Coaching Community | Network with Certified Coaches',
    ogDescription: 'Join the exclusive NEXGEN Executive Coaching Academy community. Connect, collaborate, and grow with fellow ICF-aligned executive coaches. Access resources, peer support & networking for your coaching practice.',
    ogType: 'website',
    twitterTitle: 'NEXGEN Coaching Community | Network with Certified Coaches',
    twitterDescription: 'Join the exclusive NEXGEN Executive Coaching Academy community. Connect, collaborate, and grow with fellow ICF-aligned executive coaches. Access resources, peer support & networking for your coaching practice.',
    robots: 'index, follow'
  });


    this.currentMonth = new Date().toLocaleString('default', { month: 'long' });
    this.loadAllSessions();
  }

  ngAfterViewInit(): void {
    this.initScrollReveal();
  }

  ngOnDestroy(): void {
    this.seoService.clearSeo();
    // Clean up if needed
  }

  // ==================== DATA LOADING METHODS ====================

  loadAllSessions(): void {
  this.loadingSessions = true;
  
  this.commonService.getUpcomingCommunitySessions(1, 50, 'asc', '').subscribe({
    next: (res: any) => {
      if (res?.status === 1) {
        this.calendarSessions = res.data || [];
        this.groupSessionsByMonthKey();
        this.buildCalendarMonths();
        this.upcomingSession = this.findMostUpcomingSession(this.calendarSessions);
        this.currentMonthSession = this.calendarSessions.find(
          session => session.monthTag === this.currentMonth
        ) || null;
      }
      this.loadingSessions = false;
    },
    error: (err) => {
      console.error('Failed to load sessions:', err);
      this.toast.showError('Failed to load community sessions');
      this.loadingSessions = false;
    }
  });
}

// REPLACE THIS ENTIRE METHOD
groupSessionsByMonthKey(): void {
  this.sessionsByMonthKey = {};

  this.calendarSessions.forEach(session => {
    if (!session.sessions?.[0]?.date) return;

    const { monthName, year } = this.getEstMonthAndYear(session.sessions[0].date);
    const key = `${monthName}-${year}`;

    if (!this.sessionsByMonthKey[key]) {
      this.sessionsByMonthKey[key] = [];
    }
    this.sessionsByMonthKey[key].push(session);
  });
}

// REPLACE THIS ENTIRE METHOD
buildCalendarMonths(): void {
  const nowEst = this.getNowInEst();
  const currentMonthStart = new Date(nowEst.getFullYear(), nowEst.getMonth(), 1);

  const monthNames = ['January','February','March','April','May','June',
                      'July','August','September','October','November','December'];

  const upcomingSorted = this.calendarSessions
    .filter(s => s.sessions?.[0]?.date)
    .map(s => {
      const { monthName, year } = this.getEstMonthAndYear(s.sessions[0].date);
      return new Date(year, monthNames.indexOf(monthName), 1);
    })
    .filter(d => d >= currentMonthStart)
    .sort((a, b) => a.getTime() - b.getTime());

  const startDate = upcomingSorted.length > 0 ? upcomingSorted[0] : currentMonthStart;

  this.calendarMonths = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(startDate.getFullYear(), startDate.getMonth() + i, 1);
    this.calendarMonths.push({
      monthName: monthNames[d.getMonth()],
      year: d.getFullYear(),
      monthIndex: d.getMonth(),
      label: `${monthNames[d.getMonth()]} ${d.getFullYear()}`
    });
  }
}

getSessionsForMonthKey(monthName: string, year: number): CommunitySession[] {
  const key = `${monthName}-${year}`;
  return this.sessionsByMonthKey[key] || [];
}

// REPLACE THIS ENTIRE METHOD
isMonthKeyPast(monthName: string, year: number): boolean {
  const nowEst = this.getNowInEst();
  const monthNames = ['January','February','March','April','May','June',
                      'July','August','September','October','November','December'];
  const monthDate = new Date(year, monthNames.indexOf(monthName), 1);
  const currentMonthStart = new Date(nowEst.getFullYear(), nowEst.getMonth(), 1);
  return monthDate < currentMonthStart;
}

   // REPLACE THIS ENTIRE METHOD
findMostUpcomingSession(sessions: CommunitySession[]): CommunitySession | null {
  if (!sessions || sessions.length === 0) return null;

  const now = new Date();

  const upcomingSessions = sessions.filter(session => {
    if (!session.sessions?.[0]?.date) return false;
    const estDate = this.parseEstDateTime(session.sessions[0].date, session.sessions[0].startTime);
    return estDate > now;
  });

  if (upcomingSessions.length === 0) return null;

  return upcomingSessions.sort((a, b) => {
    const dateA = this.parseEstDateTime(a.sessions[0].date, a.sessions[0].startTime);
    const dateB = this.parseEstDateTime(b.sessions[0].date, b.sessions[0].startTime);
    return dateA.getTime() - dateB.getTime();
  })[0];
}

  // Add helper to format month and year for display
  // REPLACE THIS ENTIRE METHOD
getFormattedUpcomingMonth(): string {
  if (!this.upcomingSession?.sessions?.[0]?.date) return 'Upcoming';
  const { monthName, year } = this.getEstMonthAndYear(this.upcomingSession.sessions[0].date);
  return `${monthName} ${year}`;
}

  // Helper to get session for a specific month (returns first session or null)
  getSessionForMonth(month: string): CommunitySession | null {
  const now = new Date();
  const year = now.getFullYear();
  const sessions = this.sessionsByMonthKey[`${month}-${year}`];
  return sessions && sessions.length > 0 ? sessions[0] : null;
}

  // Check if a session is in the past
  // REPLACE THIS ENTIRE METHOD
isSessionPast(session: CommunitySession): boolean {
  if (!session?.sessions?.[0]?.date) return false;
  const estDate = this.parseEstDateTime(session.sessions[0].date, session.sessions[0].startTime);
  return estDate < new Date();
}

  // Check if a month is in the past
  isMonthPast(month: string): boolean {
  return this.isMonthKeyPast(month, new Date().getFullYear());
}

  // ==================== MODAL METHODS ====================

  openRegistrationModal(session: CommunitySession, sessionDetail: any): void {
    // Don't allow registration for past sessions
    if (this.isSessionPast(session)) {
      this.toast.showError('This session has already passed');
      return;
    }

    if (this.isFullyBooked(session)) {
      this.toast.showError('This session is fully booked');
      return;
    }

    this.selectedSession = session;
    this.selectedSessionDetail = sessionDetail;
    this.showRegistrationModal = true;
    
    // Reset form
    this.registrationForm = {
      firstName: '',
      lastName: '',
      email: '',
      agreeToTerms: false,
      emailFollowUp: false
    };
  }

  closeRegistrationModal(): void {
    this.showRegistrationModal = false;
    this.selectedSession = null;
    this.selectedSessionDetail = null;
  }

  // ==================== REGISTRATION METHODS ====================

  registerForSession(): void {
    // Validate form
    if (!this.registrationForm.firstName.trim()) {
      this.toast.showError('First name is required');
      return;
    }
    if (!this.registrationForm.lastName.trim()) {
      this.toast.showError('Last name is required');
      return;
    }
    if (!this.registrationForm.email.trim()) {
      this.toast.showError('Email is required');
      return;
    }
    if (!this.isValidEmail(this.registrationForm.email)) {
      this.toast.showError('Please enter a valid email address');
      return;
    }
    if (!this.registrationForm.agreeToTerms) {
      this.toast.showError('You must agree to the terms to continue');
      return;
    }

    if (!this.selectedSession || !this.selectedSessionDetail) {
      this.toast.showError('Session information is missing');
      return;
    }

    // Double-check session isn't past
    if (this.isSessionPast(this.selectedSession)) {
      this.toast.showError('This session has already passed');
      this.closeRegistrationModal();
      return;
    }

    this.loader.show();

    const payload = {
      communitySessionId: this.selectedSession._id,
      sessionId: this.selectedSessionDetail._id,
      firstName: this.registrationForm.firstName.trim(),
      lastName: this.registrationForm.lastName.trim(),
      email: this.registrationForm.email.trim().toLowerCase(),
      emailFollowUp: this.registrationForm.emailFollowUp
    };

    this.commonService.createCommunitySessionCheckout(payload).subscribe({
      next: (res: any) => {
        this.loader.hide();
        if (res?.url) {
          window.location.href = res.url;
        } else {
          this.toast.showError('Invalid response from server');
        }
      },
      error: (err: any) => {
        this.loader.hide();
        const errorMsg = err.error?.message || 'Failed to create checkout session';
        this.toast.showError(errorMsg);
        console.error('Checkout error:', err);
      }
    });
  }

  // ==================== HELPER METHODS ====================

  isFullyBooked(session: CommunitySession): boolean {
    return session.availableSpots <= 0;
  }

  getAvailableSpotsText(session: CommunitySession): string {
    if (session.availableSpots <= 0) return 'Fully Booked';
    if (session.availableSpots < 5) return `Only ${session.availableSpots} spots left!`;
    return `${session.availableSpots} spots available`;
  }

 formatTime(timeStr: string): string {
  if (!timeStr) return '';
  
  // Check if time is already in AM/PM format
  if (timeStr.includes('AM') || timeStr.includes('PM')) {
    return timeStr; // Return as-is
  }
  
  // Handle HH:MM format
  const [hours, minutes] = timeStr.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHour = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
  return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
}

  // REPLACE THIS ENTIRE METHOD
formatDate(dateStr: string): string {
  if (!dateStr) return '';
  return this.parseEstDateTime(dateStr).toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // ==================== SCROLL METHODS ====================

  scrollTo(sectionId: string): void {
    const el = document.getElementById(sectionId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  goToUrl(): void {
    window.location.href = 'https://nexgencoaches.com/contact-us/';
  }

  // ==================== SCROLL REVEAL ====================

  private initScrollReveal(): void {
    const revealEls = document.querySelectorAll<HTMLElement>('.reveal');
    revealEls.forEach(el => el.classList.add('will-animate'));

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );

    revealEls.forEach(el => observer.observe(el));

    setTimeout(() => {
      revealEls.forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight) {
          el.classList.add('in');
        }
      });
    }, 50);
  }

  @HostListener('document:keydown.escape')
  onEscapePress(): void {
    if (this.showRegistrationModal) {
      this.closeRegistrationModal();
    }
  }


 isUpcomingSession(session: CommunitySession): boolean {
  return this.upcomingSession?._id === session._id;
}


// ==================== EST DATE HELPERS ====================

private parseEstDateTime(dateStr: string, timeStr?: string): Date {
  if (!dateStr) return new Date(0);

  const [year, month, day] = dateStr.split('-').map(Number);
  let hours = 0, minutes = 0;

  if (timeStr) {
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      const [time, period] = timeStr.trim().split(' ');
      const [h, m] = time.split(':').map(Number);
      hours = h; minutes = m;
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
    } else {
      const [h, m] = timeStr.split(':').map(Number);
      hours = h; minutes = m;
    }
  }

  const pad = (n: number) => n.toString().padStart(2, '0');
  const isoString = `${year}-${pad(month)}-${pad(day)}T${pad(hours)}:${pad(minutes)}:00-05:00`;
  return new Date(isoString);
}

private getEstMonthAndYear(dateStr: string): { monthName: string; year: number } {
  const estDate = this.parseEstDateTime(dateStr);
  const monthNames = ['January','February','March','April','May','June',
                      'July','August','September','October','November','December'];
  const estString = estDate.toLocaleString('en-US', { timeZone: 'America/New_York' });
  const d = new Date(estString);
  return { monthName: monthNames[d.getMonth()], year: d.getFullYear() };
}

private getNowInEst(): Date {
  const nowEstStr = new Date().toLocaleString('en-US', { timeZone: 'America/New_York' });
  return new Date(nowEstStr);
}


openLatestUpcomingSessionModal(): void {
  if (!this.upcomingSession || !this.upcomingSession.sessions?.length) {
    this.toast.showError('No upcoming session available right now');
    return;
  }

  this.openRegistrationModal(this.upcomingSession, this.upcomingSession.sessions[0]);
}
}